import { useEffect, useState } from 'react';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import _sortBy from 'lodash/sortBy';
import _uniq from 'lodash/uniq';

import { get } from 'ravenjs/utils/lodash';

import { RESOURCES } from 'constants/tickets';

export function usePreppedAnswerUpdateRequests({
    updateRequests,
    preppedAnswerId,
    onUpdateRequest,
    updatePreppedAnswer,
    completeVpa,
    declineVpa,
    fetchUsers,
    isVPA,
    approveSuggestedPAUpdate,
    declineSuggestedPAUpdate,
}) {
    const [userMap, setUserMap] = useState({});
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    function formatContentString(content = []) {
        return content
            .map(
                ({ content_id = '', content_type = '', title = '' }) =>
                    `${content_id} - ${content_type} - ${title}\n`
            )
            .join('\n');
    }

    function formatResourcesString(resource = []) {
        return resource
            .map(({ body = '', link = '', type = '' }) => {
                const resourceType = _find(RESOURCES.TYPES, { value: type });
                const translatedType = resourceType?.label || type;

                if (type === RESOURCES.TYPES.LINK.value) {
                    return `${translatedType} - ${body} - ${link}\n`;
                }
                return `${translatedType} - ${body}\n`;
            })
            .join('\n');
    }

    function formatTopicsString(classifications = [], topicsSummary = {}) {
        if (_isEmpty(classifications) || _isEmpty(topicsSummary)) {
            return '';
        }

        const topicsSummaryFlattened = {};
        const summaryMicroTopics = get(topicsSummary, 'micro_topics', []);
        const summarySubTopics = get(topicsSummary, 'sub_topics', []);
        const summaryTopics = get(topicsSummary, 'topics', []);

        summaryMicroTopics.forEach(microTopic => {
            topicsSummaryFlattened[microTopic.id] = microTopic;
        });
        summarySubTopics.forEach(subTopic => {
            topicsSummaryFlattened[subTopic.id] = subTopic;
        });
        summaryTopics.forEach(topic => {
            topicsSummaryFlattened[topic.id] = topic;
        });

        const topicsStrings = _sortBy(classifications, 'position').map(classification => {
            let topicString = '';

            const topicId = classification.topic_id;
            const topicSummary = topicsSummaryFlattened[topicId];

            topicString = topicSummary.name;

            const parentId = topicSummary.parent_id;

            if (parentId && !_isEmpty(topicsSummaryFlattened[parentId])) {
                topicString = `${topicsSummaryFlattened[parentId].name} > ${topicString}`;

                const grandParentId = topicsSummaryFlattened[parentId].parent_id;

                if (grandParentId && !_isEmpty(topicsSummaryFlattened[grandParentId])) {
                    topicString = `${topicsSummaryFlattened[grandParentId].name} > ${topicString}`;
                }
            }

            return topicString;
        });

        return topicsStrings.join('\n\n');
    }

    function getAcceptUpdatePayload(id) {
        const updateRequest = isVPA
            ? _find(updateRequests, { vpaRequestId: id })
            : _find(updateRequests, { suggestedUpdateId: id });
        const body = get(updateRequest, 'requested_changes.new_body', null);
        const contentLinks = get(updateRequest, 'requested_changes.new_contents', []);
        const keyPhrases = get(updateRequest, 'requested_changes.new_key_phrases', null);
        const regions = get(updateRequest, 'requested_changes.new_regions', null);
        const title = get(updateRequest, 'requested_changes.new_title', null);
        const resourceAttributes = get(
            updateRequest,
            'requested_changes.new_resources_attributes',
            null
        );

        const classificationsAttributes = get(
            updateRequest,
            'requested_changes.new_classifications',
            null
        );

        const revisedContentLinks = contentLinks.map(contentLink => {
            return {
                genesis_content_id: contentLink.content_id,
                genesis_content_type: contentLink.content_type,
                document_type: contentLink.documentType,
            };
        });

        const attributes = {
            body: body || undefined,
            classifications_attributes: classificationsAttributes || undefined,
            content_links: revisedContentLinks || undefined,
            key_phrases: keyPhrases || undefined,
            regions: regions || undefined,
            resources_attributes: resourceAttributes || undefined,
            title: title || undefined,
        };

        const updatePayload = {
            data: {
                type: 'prepped_answers',
                id: preppedAnswerId,
                attributes,
            },
        };

        return updatePayload;
    }

    async function onAcceptVpa(vpaRequestId, onCompleteRequest) {
        try {
            setLoading(true);
            const updatePayload = getAcceptUpdatePayload(vpaRequestId);

            await updatePreppedAnswer({ data: updatePayload, id: preppedAnswerId });
            await completeVpa({ id: vpaRequestId });
            onUpdateRequest();
            setLoading(false);
            onCompleteRequest(true);
        } catch (e) {
            setLoading(false);
            onCompleteRequest(false);
        }
    }

    async function onDeclineVpa(vpaRequestId, onCompleteRequest) {
        try {
            setLoading(true);
            await declineVpa({ id: vpaRequestId });

            onUpdateRequest();
            setLoading(false);
            onCompleteRequest(true);
        } catch (e) {
            setLoading(false);
            onCompleteRequest(false);
        }
    }

    async function onApproveSuggestedPAUpdate(suggestedUpdateId, onCompleteRequest) {
        try {
            setLoading(true);

            const updatePayload = getAcceptUpdatePayload(suggestedUpdateId);
            await updatePreppedAnswer({ data: updatePayload, id: preppedAnswerId });
            await approveSuggestedPAUpdate({ id: suggestedUpdateId, preppedAnswerId });
            onUpdateRequest();
            setLoading(false);
            onCompleteRequest(true);
        } catch (e) {
            setLoading(false);
            onCompleteRequest(false);
        }
    }

    async function onDeclineSuggestedPAUpdate(suggestedUpdateId, onCompleteRequest) {
        try {
            setLoading(true);
            await declineSuggestedPAUpdate({ id: suggestedUpdateId, preppedAnswerId });

            onUpdateRequest();
            setLoading(false);
            onCompleteRequest(true);
        } catch (e) {
            setLoading(false);
            onCompleteRequest(false);
        }
    }

    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true);

                let userIds = [];
                const userMap = {};

                updateRequests.forEach(({ requester_id }) => {
                    userIds = _uniq([...userIds, requester_id]);
                });

                const usersResponse = await fetchUsers({ userId: userIds });
                const users = get(usersResponse, 'users', []);
                users.forEach(user => {
                    const { userId } = user;
                    userMap[userId] = user;
                });

                setUserMap(userMap);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [fetchUsers, updateRequests]);

    return {
        error,
        formatContentString,
        formatResourcesString,
        formatTopicsString,
        loading,
        onAcceptVpa,
        onDeclineVpa,
        onApproveSuggestedPAUpdate,
        onDeclineSuggestedPAUpdate,
        userMap,
    };
}
